import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PreTradeResponseData } from '../../redux/preTradeApi';
import { PreTradeTabType, type OpenScenario, type PreTradeRouteState } from '../../types';

interface UseScenarioTabsResult {
  activeTab: PreTradeTabType;
  activeScenarioIndex: number;
  activeScenario: PreTradeResponseData | undefined;
  openScenarios: OpenScenario[];
  setActiveTab: (tab: PreTradeTabType) => void;
  selectScenario: (index: number) => void;
  showDetails: (index: number) => void;
  closeTab: (index: number) => void;
}

interface UseScenarioTabsParams {
  scenarios: PreTradeResponseData[];
  sessionId: string;
  routeState?: PreTradeRouteState;
  onTabChange?: (tab: PreTradeTabType, requestId?: string) => void;
}

export function useScenarioTabs({
  scenarios,
  sessionId,
  routeState,
  onTabChange,
}: UseScenarioTabsParams): UseScenarioTabsResult {
  const [activeTab, setActiveTab] = useState<PreTradeTabType>(PreTradeTabType.Scenarios);
  const [activeScenarioIndex, setActiveScenarioIndex] = useState(0);
  // Tracks which scenario detail tabs are opened (by their index in the scenarios array)
  const [openTabIndices, setOpenTabIndices] = useState<Set<number>>(new Set());
  // Distinguishes first hydration (0 -> N) from new scenario additions.
  const prevScenariosLengthRef = useRef(0);

  // Resets tab state when navigating to a different session.
  const prevSessionIdRef = useRef(sessionId);

  // Prevents re-applying the same deep-link when unrelated deps change.
  const lastAppliedRouteKeyRef = useRef<string | null>(null);

  // Stable reference so the effect doesn't re-trigger on every render.
  const normalizedRouteState = useMemo(() => normalizeRouteState(routeState), [routeState]);
  const { openTab, requestId } = normalizedRouteState;

  // Dedup key so the effect skips already-applied deep-links.
  const hasRouteState = openTab != null || requestId != null;
  const routeStateKey = hasRouteState ? `${sessionId}:${openTab}:${requestId}` : null;

  useEffect(() => {
    const allIndices = new Set(Array.from({ length: scenarios.length }, (_unused, i) => i));

    // New session: reset UI and seed open tabs so comparison rows stay populated.
    if (prevSessionIdRef.current !== sessionId) {
      prevSessionIdRef.current = sessionId;
      prevScenariosLengthRef.current = scenarios.length;
      lastAppliedRouteKeyRef.current = null;
      setActiveTab(PreTradeTabType.Scenarios);
      setActiveScenarioIndex(0);
      setOpenTabIndices(allIndices);
      return;
    }

    const prevLength = prevScenariosLengthRef.current;
    const currentLength = scenarios.length;

    // Applies the URL's route state to the hook. Returns true if applied, false if skipped.
    const applyRouteState = () => {
      if (currentLength === 0 || routeStateKey == null || routeStateKey === lastAppliedRouteKeyRef.current) {
        return false;
      }

      if (openTab === PreTradeTabType.History || openTab === PreTradeTabType.Scenarios) {
        setActiveTab(openTab);
        lastAppliedRouteKeyRef.current = routeStateKey;
        return true;
      }

      if (openTab === PreTradeTabType.Details) {
        const deepLinkedScenarioIndex =
          requestId == null ? scenarios.length - 1 : scenarios.findIndex(scenario => scenario.RequestID === requestId);

        if (deepLinkedScenarioIndex < 0) {
          return false;
        }

        setActiveScenarioIndex(deepLinkedScenarioIndex);
        setOpenTabIndices(prev => new Set(prev).add(deepLinkedScenarioIndex));
        setActiveTab(PreTradeTabType.Details);
        lastAppliedRouteKeyRef.current = routeStateKey;
        return true;
      }

      return false;
    };

    // First hydration for an existing session: open all current scenarios in the tab rail.
    if (prevLength === 0 && currentLength > 0) {
      setOpenTabIndices(allIndices);
      const isRouteStateApplied = applyRouteState();
      if (!isRouteStateApplied) {
        setActiveScenarioIndex(currentLength - 1);
        setActiveTab(PreTradeTabType.Scenarios);
      }
      prevScenariosLengthRef.current = currentLength;
      return;
    }

    applyRouteState();

    if (currentLength > prevLength) {
      // New scenario was added, select it as the active index, add tab to tab bar, but stay on or go to Scenarios view
      const newScenarioIndex = currentLength - 1;
      setActiveScenarioIndex(newScenarioIndex);
      setOpenTabIndices(prev => new Set(prev).add(newScenarioIndex));
      setActiveTab(PreTradeTabType.Scenarios);
    }

    // Persist the latest length so we can detect true additions on the next pass.
    prevScenariosLengthRef.current = currentLength;
  }, [openTab, requestId, routeStateKey, scenarios, sessionId]);

  const activeScenario = scenarios[activeScenarioIndex];

  const openScenarios = useMemo<OpenScenario[]>(() => {
    return scenarios
      .map((scenario, index) => ({ scenario, originalIndex: index }))
      .filter(({ originalIndex }) => openTabIndices.has(originalIndex));
  }, [scenarios, openTabIndices]);

  const selectScenario = useCallback((index: number) => {
    setActiveScenarioIndex(index);
  }, []);

  const showDetails = useCallback((index: number) => {
    setActiveScenarioIndex(index);
    setOpenTabIndices(prev => new Set(prev).add(index));
    setActiveTab(PreTradeTabType.Details);
  }, []);

  const closeTab = useCallback(
    (index: number) => {
      // Calculate remaining open tabs before updating state
      const remainingIndices = Array.from(openTabIndices)
        .filter(i => i !== index)
        .toSorted((a, b) => a - b);

      setOpenTabIndices(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });

      // If closing the active tab, navigate to another tab or scenarios
      if (activeScenarioIndex === index && activeTab === PreTradeTabType.Details) {
        if (remainingIndices.length > 0) {
          // Find the next tab to focus: prefer next, fallback to previous
          const currentPosition = Array.from(openTabIndices)
            .toSorted((a, b) => a - b)
            .indexOf(index);
          const nextIndex = remainingIndices[Math.min(currentPosition, remainingIndices.length - 1)];
          setActiveScenarioIndex(nextIndex);
          onTabChange?.(PreTradeTabType.Details, scenarios[nextIndex]?.RequestID);
        } else {
          // No more open tabs, go back to scenarios
          setActiveTab(PreTradeTabType.Scenarios);
          onTabChange?.(PreTradeTabType.Scenarios);
        }
      }
    },
    [activeScenarioIndex, activeTab, onTabChange, openTabIndices, scenarios]
  );

  return {
    activeTab,
    activeScenarioIndex,
    activeScenario,
    openScenarios,
    setActiveTab,
    selectScenario,
    showDetails,
    closeTab,
  };
}

/** The routing layer already pairs requestId with the details tab and strips it
 *  elsewhere (via resolvePreTradeRequestId), so this just trims whitespace. */
function normalizeRouteState(routeState?: PreTradeRouteState): PreTradeRouteState {
  if (!routeState) {
    return {};
  }
  return { openTab: routeState.openTab, requestId: routeState.requestId?.trim() || undefined };
}
